import React, {
	memo,
	useEffect,
	useCallback,
	useMemo,
	useState
} from 'react';
import { Tab } from 'semantic-ui-react';

import { log } from '../../../../../../services/utils';

import {
	activateStaff,
	deactivateStaff,
	getAgencyStaffList,
	getAgencyStaff
} from '../../../../../../api/nora/agencies';

import { useAuthState } from '../../../../../../contexts/AuthContext';
import {
	useAgencyState,
	useAgencyAction
} from '../../../../../../contexts/AgenciesContext';
import { useUIAction } from '../../../../../../contexts/UIContext';

import ActivateStaffCell from '../../../../../Table/Cells/ActivateStaffCell';
import Email from '../../../../../Table/Cells/Email';
import FirstAndLastName from '../../../../../Table/Cells/FirstAndLastName';
import HelpModal from '../../../../../Shared/HelpModal';
import PageHeader from '../../../../../Shared/PageHeader';
import StaffModal from './StaffModal';
import Table from '../../../../../Table';
import TableRowActions from '../../../../../Table/Cells/TableRowActions';
import multiWordFilterFn from '../../../../../Table/filterFunctions/multiWordFilterFn';

const StaffTab = () => {
	const { user: { permissions } } = useAuthState();
	const { agency } = useAgencyState();
	const { setAgencyStaff } = useAgencyAction();
	const {
		setApiErrorAlert,
		setInfobarLoader
	} = useUIAction();

	const [ employee, setEmployee ] = useState( null );

	const handleCloseStaffModal = useCallback( () => {
		setEmployee( null );
	}, [] );

	const retrieveAgencyStaff = useCallback( async () => {
		setInfobarLoader( 'Retrieving Staff List...' );

		try {
			const data = await getAgencyStaffList( agency.id );

			setAgencyStaff( data );
		} catch ( error ) {
			setApiErrorAlert( error );
		}

		setInfobarLoader( '' );
	}, [
		agency?.id,
		setApiErrorAlert,
		setInfobarLoader,
		setAgencyStaff
	] );

	useEffect( () => {
		( async () => {
			await retrieveAgencyStaff();
		} )();
	}, [ retrieveAgencyStaff ] );

	const firstAndLastNameCell = useCallback( ( { row } ) => <FirstAndLastName row={ row.original } />, [] );

	const emailCell = useCallback( ( { row } ) => <Email email={ row.original.email } />, [] );

	const onView = useCallback( async ( { id } ) => {
		setInfobarLoader( 'Retrieving Staff Info...' );

		try {
			const employee = await getAgencyStaff( agency.id, id );

			setEmployee( employee );
		} catch ( error ) {
			setApiErrorAlert( error );
		}

		setInfobarLoader( '' );
	}, [
		agency.id,
		setApiErrorAlert,
		setInfobarLoader
	] );

	const onActivate = useCallback( async ( { id, statusID } ) => {
		try {
			if ( statusID === 5 ) {
				await activateStaff( agency.id, id );
			} else {
				await deactivateStaff( agency.id, id );
			}

			await retrieveAgencyStaff();
		} catch ( error ) {
			setApiErrorAlert( error );
		}
	}, [
		agency.id,
		retrieveAgencyStaff,
		setApiErrorAlert
	] );

	const activateStaffCell = useCallback( ( { row } ) => (
		<ActivateStaffCell
			row={ row.original }
			onActivate={ onActivate }
		/>
	), [ onActivate ] );

	const activateStaffHeader = useCallback(
		() => (
			<div
				style={ {
					textAlign: 'center',
					width: '100%'
				} }
			>
			Active
			</div>
		),
		[]
	);

	// The permissions will can to editgencies and once the staff endpoints and the staff interface are in place
	const tableRowActionsCell = useCallback( ( { row } ) => {
		const actions = [
			{
				action: onView,
				componentProps: { color: 'pink' },
				disable: !permissions.viewContacts.canExecute,
				icon: 'search',
				permission: permissions.viewContacts.isAvailable,
				text: 'View Staff Member'
			}
		];

		return (
			<TableRowActions
				actions={ actions }
				row={ row.original }
			/>
		);
	}, [
		onView,
		permissions
	] );

	const columns = useMemo( () => [
		{
			id: 'name',
			accessor: row => `${ row.firstName }${ row.lastName }`,
			Header: 'Name',
			filter: multiWordFilterFn,
			Cell: firstAndLastNameCell,
			minWidth: 60
		},
		{
			accessor: 'title',
			Header: 'Title',
			filter: multiWordFilterFn,
			minWidth: 60
		},
		{
			id: 'email',
			accessor: row => `${ row.email }`,
			filter: multiWordFilterFn,
			Header: 'Email',
			Cell: emailCell,
			minWidth: 250
		},
		{
			id: 'phone',
			accessor: 'phone',
			filter: multiWordFilterFn,
			Header: 'Phone',
			minWidth: 60
		},
		{
			accessor: 'activate',
			Header: activateStaffHeader,
			Cell: activateStaffCell,
			disableFilters: true,
			disableResizing: true,
			width: 100,
			minWidth: 100,
			maxWidth: 100
		}
	], [
		activateStaffCell,
		activateStaffHeader,
		emailCell,
		firstAndLastNameCell
	] );

	const actionColumn = useMemo( () => ( {
		id: 'id_actions',
		Cell: tableRowActionsCell,
		disableFilters: true,
		disableResizing: true,
		width: 100,
		minWidth: 100,
		maxWidth: 100
	} ), [ tableRowActionsCell ] );

	const renderColumns = useMemo(
		() => permissions.viewContacts.isAvailable
			? [
				...columns,
				actionColumn
			]
			: columns,
		[
			actionColumn,
			columns,
			permissions
		]
	);

	const addStaff = useCallback( () => {
		setEmployee( {} );
	}, [] );

	const staffActions = useMemo( () => [
		{
			action: addStaff,
			disable: !permissions.editAgencies.canExecute,
			icon: 'add',
			permission: permissions.editAgencies.isAvailable,
			text: 'Add Staff'
		}
	], [
		addStaff,
		permissions
	] );

	return (
		<Tab.Pane as="div">
			{ log( 'staffTab' ) }
			<PageHeader
				actions={ staffActions }
				title={
					<>
						<span>Staff Directory</span>
						<HelpModal
							id={ 30 }
							triggerStyles={ { verticalAlign: 'top' } }
						/>
					</>
				}
			/>
			<div className="table-container">
				{ agency?.staff.length
					? <Table
						columns={ renderColumns }
						data={ agency.staff }
						showCount={ agency.staff.length > 10 }
						showEmptyRows={ false }
						showPagination={ agency.staff.length > 10 }
					/>
					: <p>No records found.</p>
				}
			</div>
			<StaffModal
				open={ Boolean( employee ) }
				closeModal={ handleCloseStaffModal }
				employee={ employee }
				onSave={ retrieveAgencyStaff }
			/>
		</Tab.Pane>
	);
};

export default memo( StaffTab );
